import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '../lib/auth';
import {
  type Effort,
  type Model,
  clampEffortForModel,
  clampModelToAllowed,
  getPrefsVersion,
  readEffort,
  readModel,
  subscribePrefs,
  writeEffort,
  writeModel,
} from '../lib/settings';

/**
 * The profile's current model + reasoning level, shared by the header
 * switcher and the composer. Reads are clamped to the allowlist and
 * to what the model supports, so a stale stored value never leaks.
 */
export function useModelChoice(): {
  model: Model;
  effort: Effort;
  allowed: string[] | null;
  setModel: (model: Model) => void;
  setEffort: (effort: Effort) => void;
} {
  const { user } = useAuth();
  const profileId = user?.id ?? null;
  const allowed = user?.allowed_models ?? null;
  useSyncExternalStore(subscribePrefs, getPrefsVersion);
  const model = clampModelToAllowed(readModel(profileId), allowed);
  const effort = clampEffortForModel(readEffort(model, profileId), model);

  const setModel = useCallback(
    (next: Model) => {
      const m = clampModelToAllowed(next, allowed);
      writeModel(m, profileId);
    },
    [allowed, profileId],
  );
  const setEffort = useCallback(
    (next: Effort) => writeEffort(clampEffortForModel(next, model), profileId),
    [model, profileId],
  );
  return { model, effort, allowed, setModel, setEffort };
}
